import React from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import Layout from "./Layout";
import { branches } from "../data/branches";

const BranchesPage = () => {
  const navigate = useNavigate();
  
  return (
    <Layout>
      <main className="w-full">
      {/* Hero Section (unified style) */}
      <section className="relative h-[60vh] overflow-hidden -mt-24">
        <img 
          src="/images/Branchpage.png" 
          alt="Visit Our Branches" 
          className="absolute top-0 left-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/20" />
        <div className="absolute inset-0 flex items-center z-10">
          <div className="container mx-auto">
            <div className="text-white max-w-2xl px-8 md:px-16">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 text-left">Visit Our Branches</h1>
              <p className="text-lg md:text-xl">
                Find a branch near you and experience our services closer to home. We're proud to serve you across multiple locations with the same commitment and care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Branches Section */}
      <section className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-8 items-start">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 mt-16">Our Branches</h2>
          <p className="text-gray-700 leading-relaxed text-sm">
            British Way English Academy takes pride in being Sri Lanka's largest and most trusted English training institute, with 14 branches island-wide. Each of our branches is designed to deliver the same standard of excellence in English education, offering modern classrooms, advanced facilities, and a supportive environment for students. With highly qualified lecturers and a consistent teaching methodology, we ensure that no matter which branch you choose, you will experience the same quality learning journey. Our 14 branches make us easily accessible to students across the country, empowering thousands every year to achieve academic, professional, and personal success.
          </p>
        </div>

        <div className="grid grid-cols-0 gap-8">
          <img src="/images/aboutus.png" alt="Branch gallery" className="" />
        </div>

      </section>

      {/* Find Your Nearest Branch Section */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Find Your Nearest Branch</h3>
          <p className="text-gray-600">Get in touch or visit the branch closest to you for personalized support.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {branches.map((branch) => (
            <div 
              key={branch.id}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer"
              onClick={() => navigate(`/branch/${branch.id}`)}
            >
              <h4 className="font-semibold text-gray-900 mb-4">{branch.name}</h4>
              
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  </div>
                  <span className="text-gray-600">8:00 am to 5 pm (Daily)</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-blue-600" />
                  <span className="text-gray-700">{branch.phone}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-blue-600" />
                  <span className="text-gray-700">{branch.landline}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-600" />
                  <span className="text-gray-700">{branch.email}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-blue-600" />
                  <span className="text-gray-700 text-xs">{branch.address}</span>
                </div>
              </div>
              
              <div className="mt-4 flex justify-end">
                <a 
                  href={branch.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                  onClick={(e) => e.stopPropagation()}
                >
                  View on Map →
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Connect with nearest branch Section */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Connect with nearest branch</h3>
          <p className="text-gray-600">Connect with us today to explore our courses and find the right path for your future.</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <input
                type="text"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your name"
              />
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your email"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone number *
                </label>
                <input
                  type="tel"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your phone number"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              <textarea
                rows="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your message"
              ></textarea>
            </div>
            
            <div className="text-center">
              <button
                type="submit"
                className="px-8 py-3 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition-colors"
              >
                SEND
              </button>
            </div>
          </form>
        </div>
      </section>
      </main>
    </Layout>
  );
};

export default BranchesPage;
